import {
  cloneDeep,
  filter,
  forEach,
  get,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  map,
  round
} from 'lodash';
import SimpleSchema from 'simpl-schema';
import turf from '@turf/turf';

import {
  getBearingToPoint,
  getDistanceToPoint,
  getDestinationPoint,
  pointsCollectionFeatureToLineString,
  featurePointGetLatitude,
  featurePointGetLongitude,
  featurePointSetAltitude,
  featureCollectionPoints2KML,
  featureLinestring2KML,
  featurePolygon2KML
} from './geojson-utilities';
import getElevationFromServer from './get-elevation';

/**
 * @typedef {object} FeatureCollection
 * @typedef {object} Feature
 * @typedef {object} LatLng
 * @typedef {object} Point
 * @typedef {object} LineString
 * @typedef {object} Geometry
 * @typedef {object} Polygon
 */

/**
 * Returns a FeatureCollection with the waypoints numbers (web and total) calculated.
 *
 * @param {FeatureCollection<Point>} waypointList - GeoJSON FeatureCollection with the waypoints.
 * @returns {FeatureCollection<Point>} GeoJSON FeatureCollection with the waypoints' numbers calculated.
 */
export const setWaypointNumbers = waypointList => {
  const waypointArray = map(waypointList.features, (feature, index) => {
    const featureCopy = cloneDeep(feature);
    featureCopy.properties.totalNumber = index;
    return featureCopy;
  });
  return turf.featureCollection(waypointArray);
};

/**
 * Checks if the waypoint reference altitude is DTM.
 *
 * @param {string} waypointReferenceAltitude - Waypoint reference altitude value.
 * @returns {boolean} Returns true if the waypoint reference altitude is DTM and false otherwise.
 */
export const isDTM = waypointReferenceAltitude => waypointReferenceAltitude === 'DTM';

/**
 * Checks if the waypoint reference altitude is Take Off GPS.
 *
 * @param {string} waypointReferenceAltitude - Waypoint reference altitude value.
 * @returns {boolean} Returns true if the waypoint reference altitude is Take Off GPS and false otherwise.
 */
export const isTakeOffGPS = waypointReferenceAltitude => waypointReferenceAltitude === 'TakeOffGPS';

/**
 * @typedef {object} OperationResult
 * @property {string} operation The type of the operations are add, move, delete or none.
 * @property {number} index The index of the waypoint deleted,
 * the position to add the new waypoint or the index of the waypoint moved.
 * @property {number} lat - For move and add operations the new latitude.
 * @property {number} lon - For move and add operations the new longitude.
 * Default value { operation: 'none', index: -1, lat: -1, lon: -1 }.
 */

/**
 * Compares the old waypoint list and the new waypoint list to figure out
 * the type of operation that occurred after edition.
 *
 * @param {Feature<LineString>} currentRPALineString - A linestring that represents the waypoint list.
 * @param {LatLng[]} newRPALatLngs - Leaflet LatLng Array with the edited waypoint list.
 * @returns {OperationResult} The object representing the result fo the operation.
 */
export const getOperationType = (currentRPALineString, newRPALatLngs) => {
  const oldWaypointArray = turf.getCoords(currentRPALineString);
  const oldMinusNew = oldWaypointArray.length - newRPALatLngs.length;
  const result = {
    operation: 'none',
    index: -1,
    lat: -1,
    lon: -1
  };

  switch (oldMinusNew) {
    case 0:
      for (let wpIndex = 0; wpIndex < newRPALatLngs.length; wpIndex += 1) {
        if (
          newRPALatLngs[wpIndex].lng !== oldWaypointArray[wpIndex][0] ||
          newRPALatLngs[wpIndex].lat !== oldWaypointArray[wpIndex][1]
        ) {
          result.index = wpIndex;
          result.operation = 'move';
          result.lat = newRPALatLngs[wpIndex].lat;
          result.lon = newRPALatLngs[wpIndex].lng;
          break;
        }
      }
      break;
    case -1:
      for (let wpIndex = 0; wpIndex < oldWaypointArray.length; wpIndex += 1) {
        if (
          newRPALatLngs[wpIndex].lng !== oldWaypointArray[wpIndex][0] ||
          newRPALatLngs[wpIndex].lat !== oldWaypointArray[wpIndex][1]
        ) {
          result.index = wpIndex;
          result.operation = 'add';
          result.lat = newRPALatLngs[wpIndex].lat;
          result.lon = newRPALatLngs[wpIndex].lng;
          break;
        }
      }
      break;
    case 1:
      for (let wpIndex = 0; wpIndex < newRPALatLngs.length; wpIndex += 1) {
        if (
          newRPALatLngs[wpIndex].lng !== oldWaypointArray[wpIndex][0] ||
          newRPALatLngs[wpIndex].lat !== oldWaypointArray[wpIndex][1]
        ) {
          result.index = wpIndex;
          result.operation = 'delete';
          break;
        }
      }
      break;
    default:
      console.error('something went wrong');
      break;
  }
  return result;
};

/**
 * Changes a waypoint type to a new value and updates the numbers.
 *
 * @param {FeatureCollection<Point>} waypointList - GeoJSON FeatureCollection with the waypoints.
 * @param {number} waypointIndex - The index of the waypoint to edit.
 * @param {number} newWayPointType - The new type of waypoint.
 * @returns {FeatureCollection<Point>} A new GeoJSON FeatureCollection with the waypoint type edited.
 */
export const editWayPointType = (waypointList, waypointIndex, newWayPointType) => {
  const newWaypointList = cloneDeep(waypointList);
  newWaypointList.features[waypointIndex].properties.type = newWayPointType;
  return newWaypointList;
};

/**
 * Inserts the waypoint(s) starting at the specified index.
 *
 * @param {FeatureCollection<Point>} waypointList - GeoJSON FeatureCollection with the waypoint list.
 * @param {number} insertIndex - The index to insert the waypoint.
 * @param {Feature<Point>|(Feature<Point>[])} newWaypoints - The new feature or array of features of waypoint to insert.
 * @returns {FeatureCollection<Point>} A new GeoJSON FeatureCollection with the waypoints inserted.
 */
export const insertNewWaypointsAtIndex = (waypointList, insertIndex, newWaypoints) => {
  const newWaypointList = cloneDeep(waypointList);
  if (isArray(newWaypoints)) newWaypointList.features.splice(insertIndex, 0, ...newWaypoints);
  else newWaypointList.features.splice(insertIndex, 0, newWaypoints);
  return newWaypointList;
};

/**
 * Removes the number waypoint/s starting at the specified index.
 *
 * @param {FeatureCollection<Point>} waypointList - GeoJSON FeatureCollection with the waypoint list.
 * @param {number} waypointIndex - The index of the waypoint to remove.
 * @param {number} numberOfWaypointsToDelete - The number of waypoints to delete starting at the previous index.
 * @returns {FeatureCollection<Point>} A new GeoJSON FeatureCollection with the waypoints removed.
 */
export const removeWaypoint = (waypointList, waypointIndex, numberOfWaypointsToDelete = 1) => {
  const newWaypointList = cloneDeep(waypointList);
  newWaypointList.features.splice(waypointIndex, numberOfWaypointsToDelete);
  return newWaypointList;
};

/**
 * Moves a point in the waypoint list to another position.
 *
 * @param {FeatureCollection<Point>} waypointList - GeoJSON FeatureCollection with the waypoints.
 * @param {number} waypointIndex - The index of the waypoint moved.
 * @param {Feature<Point>} movedWaypoint - The Feature with the moved waypoint.
 * @returns {FeatureCollection<Point>} A new GeoJSON FeatureCollection with the waypoints changed.
 */
export const moveWaypoint = (waypointList, waypointIndex, movedWaypoint) => {
  const newWaypointList = cloneDeep(waypointList);
  newWaypointList.features.splice(waypointIndex, 1, movedWaypoint);
  return newWaypointList;
};

/**
 * Returns the bearing for landing in degrees.
 * The angle measured in degrees from the north line (0 degrees).
 *
 * @param {Feature<LineString>} landingBearing - A GeoJSON Feature representing the landing bearing.
 * @returns {number} The landing bearing in degrees.
 * @throws Error if landingBearing is not a feature linestring
 * bearing in decimal degrees, between -180 and 180 degrees (positive clockwise).
 */
export const getLandingBearing = landingBearing => {
  turf.featureOf(landingBearing, 'LineString', 'getLandingBearing');
  const landingBearingCoords = turf.getCoords(landingBearing);
  // index 0 is the landing point
  return turf.bearing(landingBearingCoords[0], landingBearingCoords[1]);
};

/**
 * Get the height AGL (Above Ground Level) for the waypoint.
 *
 * @param {Feature<Point>} waypoint - A feature representing a waypoint.
 * @returns {number} Height AGL (Above Ground Level) for the waypoint in meters.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointHeight = waypoint => {
  turf.featureOf(waypoint, 'Point', 'getWaypointHeight');
  return waypoint.properties.altAbsolute;
};

/**
 * Gets the ground elevation for a given waypoint (AMSL).
 * The geoid elevation. Which is Mean Sea Level.
 *
 * @param {Feature} waypoint - A feature representing a waypoint.
 * @returns {number} The ground elevation ot a given waypoint feature in meters.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointGroundElevation = waypoint => {
  turf.featureOf(waypoint, 'Point', 'getWaypointGroundElevation');
  return waypoint.properties.altGround;
};

/**
 * Calculates the altitude of the waypoint provided.
 * That is the height AMSL (Above Mean Sea Level).
 *
 * @param {Feature<Point>} waypoint - A feature representing a waypoint in meters.
 * @param {string} waypointReferenceAltitude - A feature representing a waypoint in meters.
 * @param {number} [takeOffElevation=-999] - The take off elevation used for the calculation.
 * @returns {number} The altitude for that waypoint having into account the frame of reference.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointAltitude = (
  waypoint,
  waypointReferenceAltitude,
  takeOffElevation = -999
) => {
  turf.featureOf(waypoint, 'Point', 'getWaypointAltitude');
  let waypointAltitude = getWaypointHeight(waypoint);
  if (isDTM(waypointReferenceAltitude)) {
    if (getWaypointGroundElevation(waypoint) !== -999) {
      waypointAltitude += getWaypointGroundElevation(waypoint);
    }
  } else if (isTakeOffGPS(waypointReferenceAltitude) && takeOffElevation !== -999) {
    waypointAltitude += takeOffElevation;
  } else if (takeOffElevation !== -999) {
    waypointAltitude += takeOffElevation;
  }
  return waypointAltitude;
};

/**
 * Gets the number for that waypoint.
 *
 * @param {Feature<Point>} waypoint - A feature representing a waypoint.
 * @returns {number} The number in the sequence for that waypoint.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointNumber = waypoint => {
  turf.featureOf(waypoint, 'Point', 'getWaypointNumber');
  return waypoint.properties.totalNumber;
};

/**
 * Gets the type for that waypoint.
 *
 * @param {Feature<Point>} waypoint - A feature representing a waypoint.
 * @returns {number} The number representing the type for that waypoint.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointType = waypoint => {
  turf.featureOf(waypoint, 'Point', 'getWaypointType');
  return waypoint.properties.type;
};

/**
 * Gets the distance travelled from take off for each waypoint.
 *
 * @param {Feature} waypoint - A feature representing a waypoint.
 * @returns {number} The distance travelled from take off along the route in meters.
 * @throws Error if waypoint is not a feature point.
 */
export const getWaypointDistanceFromTakeOff = waypoint => {
  turf.featureOf(waypoint, 'Point', 'getWaypointDistanceFromTakeOff');
  return waypoint.properties.distanceTraveled;
};

/**
 * Creates the array of feature points for the landing path. The landing path created depends on the
 * waypoint reference altitude.
 *
 * Reference altitude: TAKEOFF GPS.
 * The height for these waypoints is going to be added to the take off elevation to define the total
 * altitude of each waypoint.
 * The height can be positive or negative. We take into account if the landing point GPS is also set
 * since we calculate the landing path from 0 (take off elevation) or from the elevation defined in
 * the landing point.
 * Each waypoint has a total altitude which is it's Height + TakeOff Elevation.
 *
 * Reference altitude: DTM.
 * The height for these waypoints is going to be added to the ground elevation for each waypoint.
 * This value is always positive and represents the height AGL for each waypoint.
 * Each waypoint has a total altitude which is its Height + Ground Elevation (for each waypoint).
 *
 * The first waypoint in the landing path is always computed so that the desired landing bearing is achieved.
 * The calculation is then done via a loop and has to cover 2 cases.
 * For each case either the condition has to be met to exit:
 * CASE 1: last waypoint above first waypoint in landing path.
 * - If the slope with the last waypoint > -(the required slope).
 * CASE 2: last waypoint below first waypoint in landing path.
 * - If the slope with the last waypoint < the required slope.
 *
 * While those conditions are not met, the loop does this:
 * 1. Generates the next landing path point, taking the last waypoint in the array,
 * the segment distance and the bearing.
 * 2. Updates the bearing for the next WP.
 * 3. Updates the current height and adds the new WP to the landing path array.
 *
 * @async
 * @param {object} arg - The arguments for the function.
 * @param {Feature<Point>} arg.landingPointFeature - A GeoJSON Feature with the landing point.
 * @param {number} arg.landingBearing - The bearing in degrees measured from magnetic north for the landing.
 * @param {number} arg.segmentSize - The size of the segment for the landint path square in metres.
 * @param {number} arg.landingSlope - The slope for the landing path in percentage (positive).
 * @param {Feature<Point>} arg.lastWaypointFeature - The feature of the las waypoint in the mission before landing.
 * @param {string} arg.waypointReferenceAltitude - The altitude reference for the waypoint altitudes.
 * @param {boolean} arg.isClockWise - Flag to indicate the rotation of the landing path.
 * @param {string} arg.missionId - The mission id for this calculation.
 * @returns {Feature<Point>[]} A new array of Feature Points with the landing path.
 * Not including the landing point.
 */
export const calculateLandingPath = async ({
  landingPointFeature,
  landingBearing,
  segmentSize,
  landingSlope,
  lastWaypointFeature,
  waypointReferenceAltitude,
  isClockWise,
  missionId
}) => {
  // Variable Declaration - Common.
  /** @member {Feature<Point>[]} */
  this.landingPathFeaturePoints = [];
  /** @member {number} */
  this.currentBearing = landingBearing;
  /** @member {number} */
  this.currentHeight = 0;
  /** @member {boolean} */

  const heightPerSegment = (segmentSize * landingSlope) / 100;
  const lastWPHeight = getWaypointHeight(lastWaypointFeature);
  const lastWPAltitude = getWaypointAltitude(lastWaypointFeature, waypointReferenceAltitude);
  // this way the lading path will build up from the landing point.
  if (isTakeOffGPS(waypointReferenceAltitude)) {
    // if the landing has ground elevation defined by GPS, we need to have it into account
    // the height for the landing point is relative to the takeoff point
    // but has into account the GPS value for the landing point height already
    // FIXME: something is still off.. but maybe now less
    // the current height is Above TakeOff Level
    this.currentHeight = getWaypointHeight(landingPointFeature);
  } else if (isDTM(waypointReferenceAltitude)) {
    // the current height is AMSL
    this.currentHeight = getWaypointAltitude(landingPointFeature, waypointReferenceAltitude);
  }

  let heightForLoop = 0;
  if (isTakeOffGPS(waypointReferenceAltitude)) {
    heightForLoop = lastWPHeight;
  } else if (isDTM(waypointReferenceAltitude)) {
    heightForLoop = lastWPAltitude;
  }

  // FIXME: problema con SET landing GPS, se debe hacer primero el set landing. ¿?

  /**
   * Updates the current bearing used for generating the landing path, it uses the
   * clockwise flag for determining in which direction we rotate the bearing.
   *
   * @this calculateLandingPath
   * @modifies {calculateLandingPath.currentBearing}.
   */
  const updateBearing = () => {
    this.currentBearing += isClockWise ? -90 : 90;
    if (this.currentBearing > 180) this.currentBearing -= 360;
    else if (this.currentBearing < -180) this.currentBearing += 360;
  };

  /**
   * Adds the new waypoint to the landing path stack (at index 0).
   * Updates the current height by adding or substracting the step height.
   * Adds the following properties to the feature point: type, isLandingPath, altAbsolute:
   * - type = 5
   * - isLandingPath = true.
   *
   * Reference altitude: TAKEOFF GPS.
   * - Current height is AGL (Ground Level = Take Off)
   * - altAbsolute (AGL) = current height
   * - CASE UP if ( isNotFirst && current height > height of last waypoint in mission)
   * - CASE DOWN If (isNotFirst && current height < height of last waypoint in mission)
   * - altAbsolute (AGL) = height of last waypoint in mission.
   *
   * Reference altitude: DTM.
   * - Current height is AMSL.
   * - altAbsolute (AGL) = current height - altGround
   * - CASE UP if (isNotFirst && current height > altitude of the last waypoint in mission)
   * - CASE DOWN if (isNotFirst && current height < altitude of the last waypoint in mission)
   * - altAbsolute (AGL) = altitude of the last waypoint in the mission - altGround.
   *
   * The last waypoint in the mission and the first point in the landing path have the same altitude.
   *
   * @this calculateLandingPath
   * @param {object} arg - The argument for the function.
   * @param {Feature<Point>} arg.nextWPinLandingPath - The next waypoint to add to the landing path.
   * @param {string} arg.direction - The direction for the waypoint, up or down.
   * @param {boolean} arg.isFirst - Flag to determine if it's first waypoint in landing path.
   * @modifies this.currentHeight - Updates the height.
   * @modifies this.landingPathFeaturePoints - Adds the new waypoint in landing path stack.
   * @modifies nextWPinLandingPath - Adds new properties to feature (type, isLandingPath, altGround, altAbsolute).
   */
  const updateHeightAndAddToPath = ({ nextWPinLandingPath, direction, isFirst }) => {
    const nextWPinLandingPathClone = cloneDeep(nextWPinLandingPath);
    switch (direction) {
      case 'up':
        this.currentHeight += heightPerSegment;
        if (isTakeOffGPS(waypointReferenceAltitude)) {
          nextWPinLandingPathClone.properties.altAbsolute = this.currentHeight;
          if (!isFirst && this.currentHeight > lastWPHeight)
            nextWPinLandingPathClone.properties.altAbsolute = lastWPHeight;
        } else if (isDTM(waypointReferenceAltitude)) {
          nextWPinLandingPathClone.properties.altAbsolute =
            this.currentHeight - getWaypointGroundElevation(nextWPinLandingPathClone);
          if (!isFirst && this.currentHeight > lastWPAltitude)
            nextWPinLandingPathClone.properties.altAbsolute =
              lastWPAltitude - getWaypointGroundElevation(nextWPinLandingPathClone);
        }
        break;
      case 'down':
        this.currentHeight -= heightPerSegment;
        if (isTakeOffGPS(waypointReferenceAltitude)) {
          nextWPinLandingPathClone.properties.altAbsolute = this.currentHeight;
          if (!isFirst && this.currentHeight < lastWPHeight)
            nextWPinLandingPathClone.properties.altAbsolute = lastWPHeight;
        } else if (isDTM(waypointReferenceAltitude)) {
          nextWPinLandingPathClone.properties.altAbsolute =
            this.currentHeight - getWaypointGroundElevation(nextWPinLandingPathClone);
          if (!isFirst && this.currentHeight < lastWPAltitude)
            nextWPinLandingPathClone.properties.altAbsolute =
              lastWPAltitude - getWaypointGroundElevation(nextWPinLandingPathClone);
        }
        break;
      default:
        console.error('You should not get here');
        break;
    }
    this.landingPathFeaturePoints.unshift(nextWPinLandingPathClone);
  };

  /**
   * Returns the last waypoint added to the landing path stack.
   * This would be the first item in the array.
   *
   * @this calculateLandingPath
   * @returns {Feature<Point>} The last waypoint added to the landing path array or the landing point
   * if the array is empty.
   */
  const getLastPointInLandingPath = () =>
    isEmpty(this.landingPathFeaturePoints) ? landingPointFeature : this.landingPathFeaturePoints[0];

  /**
   * Calculates the slope between the last waypoint in the mission and the
   * current first point in the landing path array. From the last waypoint
   * in the mission.
   *
   * Reference altitude: TAKEOFF GPS.
   * Height used: AltAbsolute for waypoint, which is AGL (Ground Level = Take Off).
   *
   * Reference altitude: DTM.
   * Height used: altitude for waypoint AMSL.
   *
   * @returns {number} The slope of the path that connects the last waypoint in
   * the mission and the first waypoint in the landing path, in %. Positive for ascending slopes
   * and negative for descending slopes.
   */
  const getSlopeWithLastWPinPath = () => {
    const distanceToPoint = getDistanceToPoint(getLastPointInLandingPath(), lastWaypointFeature);
    let deltaHeight = 0;

    // (to) - (from)
    if (isTakeOffGPS(waypointReferenceAltitude))
      deltaHeight =
        getWaypointHeight(getLastPointInLandingPath()) - getWaypointHeight(lastWaypointFeature);
    else if (isDTM(waypointReferenceAltitude))
      deltaHeight =
        getWaypointAltitude(getLastPointInLandingPath(), waypointReferenceAltitude) -
        getWaypointAltitude(lastWaypointFeature, waypointReferenceAltitude);

    return (deltaHeight / distanceToPoint) * 100;
  };

  /**
   * Create the next waypoint in the landing path, from the last point
   * in the landing path, in the direction of the bearing and at a distance given by the
   * segment size.
   *
   * @this calculateLandingPath
   * @returns {Feature<Point>} The next waypoint to be added to the landing path.
   */
  const generateNextLandingPathPoint = () =>
    getDestinationPoint(getLastPointInLandingPath(), this.currentBearing, segmentSize);

  /**
   * Add a waypoint in the landing path. Steps:
   * 1. Calculate next waypoint from last one on landing path in the bearing direction
   * 2. Fetch ground altitude data and update waypoint data
   * 3. Update current bearing
   * 4. Update current height and adds waypoint to landing path array.
   *
   * @async
   * @this calculateLandingPath
   * @param {object} arg - The arguments for the function.
   * @param {string} arg.direction - The direction for the waypoint, up or down.
   * @param {boolean} [arg.isFirst=false] - Flag to determine if it's first waypoint in landing path.
   * @modifies this.currentHeight - Updates the height.
   * @modifies this.currentBearing - Updates the bearing.
   * @modifies this.landingPathFeaturePoints - Adds the new waypoint in lading path stack.
   * @returns {Feature<Point>} The next waypoint to be added to the landing path.
   * @throws Error if the waypoint reference altitude is DTM and there is a fetch error.
   */
  const createWaypoint = async (
    { direction, isFirst } = {
      isFirst: false
    }
  ) => {
    let nextWPinLandingPath = {};
    try {
      nextWPinLandingPath = generateNextLandingPathPoint();
      nextWPinLandingPath.properties.type = 5;
      nextWPinLandingPath.properties.isLandingPath = true;
      nextWPinLandingPath = await getElevationFromServer(missionId, nextWPinLandingPath);
      [, , nextWPinLandingPath.properties.altGround] = nextWPinLandingPath.geometry.coordinates;
    } catch (error) {
      if (isDTM(waypointReferenceAltitude)) {
        throw error;
      } else {
        nextWPinLandingPath.properties.altGround = -999;
      }
    }
    updateBearing();
    updateHeightAndAddToPath({ nextWPinLandingPath, direction, isFirst });
  };

  // FIXME: Lo dejamos así por si lo volvemos a usar
  /* eslint-disable */
  // const debugLogs = () => {
  //   const heightBelowTarget = this.currentHeight < heightForLoop;
  //   const remainingHeightGreaterThanStep =
  //     Math.abs(this.currentHeight - heightForLoop) > heightPerSegment;
  //   const slopeBiggerThanLimitUP = getSlopeWithLastWPinPath() < -landingSlope;
  //   const slopeBiggerThanLimitDOWN = getSlopeWithLastWPinPath() > landingSlope;
  //   console.log(`this.currentHeight (${this.currentHeight}) < heightForLoop ${heightForLoop}`);
  //   console.log(`heightBelowTarget ${heightBelowTarget}`);
  //   console.log(
  //     `this.currentHeight - heightForLoop (${this.currentHeight -
  //       heightForLoop}) > heightPerSegment ${heightPerSegment}`
  //   );
  //   console.log(`remainingHeightGreaterThanStep ${remainingHeightGreaterThanStep}`);
  //   console.log(
  //     `getSlopeWithLastWPinPath() ${getSlopeWithLastWPinPath()} < -landingSlope ${-landingSlope}`
  //   );
  //   console.log(`slopeBiggerThanLimitUP ${slopeBiggerThanLimitUP}`);
  //   console.log(
  //     `getSlopeWithLastWPinPath() ${getSlopeWithLastWPinPath()} > landingSlope ${landingSlope}`
  //   );
  //   console.log(`slopeBiggerThanLimitDOWN ${slopeBiggerThanLimitDOWN}`);
  //   console.log(`----`);
  // };
  /* eslint-enable */

  // Add first waypoint in landing path (the one just before landing)
  // console.log('First WaypointPoint in landing path'); // eslint-disable-line
  await createWaypoint({ direction: 'up', isFirst: true });

  // debugLogs();

  if (heightForLoop > this.currentHeight)
    while (getSlopeWithLastWPinPath() < -landingSlope) {
      // landing path hacia arriba desde el aterrizaje
      // loop iterations are not independent
      await createWaypoint({ direction: 'up' }); // eslint-disable-line 
      // debugLogs();
    }
  else
    while (getSlopeWithLastWPinPath() > landingSlope) {
      // landing path hacia abajo desde el aterrizaje
      // loop iterations are not independent
      await createWaypoint({ direction: 'down' }); // eslint-disable-line 
      // debugLogs();
    }

  return this.landingPathFeaturePoints;
};

/**
 * Checks if the landing path is defined.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint List.
 * @returns {boolean} True if landing path is defined, false otherwise.
 * @throws {Error} If the waypoint list not a feature Collection of points.
 */
export const checkforLandingPath = waypointList => {
  turf.collectionOf(waypointList, 'Point', 'checkforLandingPath');
  let numberOfWPLandingPath = 0;
  forEach(waypointList.features, waypointFeature => {
    if (get(waypointFeature, 'properties.isLandingPath', false)) numberOfWPLandingPath += 1;
  });
  return numberOfWPLandingPath !== 0;
};

/**
 * Looks for the ground elevation of the take off point in the
 * waypoint list.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @returns {number} The ground elevation of the take off point.
 * @throws {Error} If the waypoint list not a feature Collection of points.
 */
export const getTakeOffElevation = waypointList => {
  turf.collectionOf(waypointList, 'Point', 'getTakeOffElevation');
  const takeOffHeight = getWaypointGroundElevation(waypointList.features[0]);
  return takeOffHeight;
};

/**
 * Looks for the last waypoint in the mission just before landing assuming there
 * is no landing path defined.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @returns {Feature<Point>} The feature point representing the waypoint before landing.
 * @throws {Error} If the waypoint list not a feature Collection of points.
 */
export const getLastWaypointFeaturePoint = waypointList => {
  turf.collectionOf(waypointList, 'Point', 'getLastWaypointFeaturePoint');
  return waypointList.features[waypointList.features.length - 2];
};

/**
 * Looks for the landing point in the waypoint list.
 *
 * @param {FeatureCollection<Point>} waypointList - Waypoint list.
 * @returns {Feature<Point>} The feature point representing the landing point.
 * @throws {Error} If the waypoint list not a feature Collection of points.
 */
export const getLandingPoint = waypointList => {
  turf.collectionOf(waypointList, 'Point', 'getLandingPoint');
  return waypointList.features[waypointList.features.length - 1];
};

/**
 * Takes in the waypoint list and return this waypoint list with the landing path added.
 *
 * @async
 * @param {object} arg - The object argument for the waypoint.
 * @param {FeatureCollection<Point>} arg.waypointList - The waypoint List.
 * @param {Feature<LineString>} arg.landingBearing - A LineString representing the landing bearing.
 * @param {number} arg.segmentSize - The size of the segment for the landing path in metres.
 * @param {number} arg.landingSlope - The slope for the landing path in percentage.
 * @param {string} arg.waypointReferenceAltitude - The waypoint reference altitude.
 * @param {boolean} arg.isClockWise - Flag to indicate the rotation of the path.
 * @param {string} arg.missionId - The mission id for this calculation, needed to check the dtm.
 * @returns {FeatureCollection<Point>|object} The waypoint list with the landing path inserted or an emtpy
 * obtject if the landing path already exists.
 * @throws Error is lading path array calculated is empty.
 */
export const addLandingPath = async ({
  waypointList,
  landingBearing,
  segmentSize,
  landingSlope,
  waypointReferenceAltitude,
  isClockWise = false,
  missionId
}) => {
  // FIXME: should we just erase it
  if (checkforLandingPath(waypointList)) return {};

  let landingPathArray = [];
  try {
    landingPathArray = await calculateLandingPath({
      landingPointFeature: getLandingPoint(waypointList),
      landingBearing: getLandingBearing(landingBearing),
      segmentSize,
      landingSlope,
      lastWaypointFeature: getLastWaypointFeaturePoint(waypointList),
      waypointReferenceAltitude,
      isClockWise,
      missionId
    });
  } catch (error) {
    throw new Error(error.message);
  }

  if (isEmpty(landingPathArray)) throw new Error('No landing Path Computed');

  let newWaypointList = insertNewWaypointsAtIndex(waypointList, -1, landingPathArray);
  newWaypointList = setWaypointNumbers(newWaypointList);
  return newWaypointList;
};

/**
 * Removes the landing path from the waypoint list.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint List.
 * @returns {FeatureCollection<Point>|object} The waypoint list without the landing path or
 an empty object if the landing ponint is not defined.
 */
export const deleteLandingPath = waypointList => {
  const newWaypointList = cloneDeep(waypointList);
  if (checkforLandingPath(newWaypointList)) {
    const featuresArray = newWaypointList.features;
    const filteredWaypointArray = filter(featuresArray, waypointFeature =>
      isNil(waypointFeature.properties.isLandingPath)
    );
    newWaypointList.features = filteredWaypointArray;
    const updateWaypointListNumbers = setWaypointNumbers(newWaypointList);
    return updateWaypointListNumbers;
  }
  return {};
};

/**
 * Takes in the waypoint list and returns the distance travelled in meters.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint List.
 * @returns {number} The total distance travelled in meters.
 */
export const getDistanceTraveled = waypointList => {
  const waypointLinestring = pointsCollectionFeatureToLineString(waypointList);
  const lenghtInKilometers = turf.length(waypointLinestring, { units: 'kilometers' });
  return lenghtInKilometers * 1000;
};

/**
 * @typedef {object} SentWaypoint https://github.com/mavlink/mavlink/blob/master/message_definitions/v1.0/common.xml#L434.
 * @property {number} x - The latitude of the waypoint.
 * @property {number} y - The longitude of the waypoint.
 * @property {number} z - The height for the waypoint.
 * @property {number} frame - The frame of reference for the coordinate system.
 * CASE 0 Global coordinate frame, WGS84 coordinate system.
 * - x: latitude
 * - y: longitude
 * - z: positive altitude over mean sea level (MSL).
 * CASE 3 - Global coordinate frame, WGS84 coordinate system, relative altitude over ground with respect to the home position.
 * - x: latitude
 * - y: longitude
 * - z: positive altitude with 0 being at the altitude of the home location.
 * @property {number} seq - The order of the waypoint in the sequence.
 * @property {number} command - The command that the waypoint runs.
 * CASE 16 - Navigate to waypoint.
 * - param1: Hold time in decimal seconds
 * - param2: Acceptance radius in meters
 * - param3: Radius in meters to pass by WP
 * - param4: Desired yaw angle at waypoint.
 * CASE 22 - Takeoff from ground / hand.
 * - param1: Minimum pitch (if airspeed sensor present), desired pitch without sensor
 * - param2: Empty
 * - param3: Empty
 * - param4: Yaw angle (if magnetometer present).
 * CASE 21 - Land at location.
 * - param1: Abort Alt
 * - param2: Precision land mode
 *   - 0 = normal landing
 *   - 1 = opportunistic precision landing
 *   - 2 = required precsion landing
 * - param3: Empty
 * - param4: Desired yaw angle.
 * CASE 206 - Mission command to set camera trigger distance for this flight.
 * The camera is triggered each time this distance is exceeded.
 * This command can also be used to set the shutter integration time for the camera.
 * - param1: Camera trigger distance (meters). 0 to stop triggering
 * - param2: Camera shutter integration time (milliseconds). -1 or 0 to ignore
 * - param3: Radius in meters to pass by WP
 * - param4: Desired yaw angle at waypoint.
 * @property {number} param1=0 @see command.
 * @property {number} param2=0 @see command.
 * @property {number} param3=0 @see command.
 * @property {number} param4=0 @see command.
 * @property {number} featureNumber - Index of correspoding waypoint.
 */
/**
 * Schema for the waypoint array that is sent to the RPA.
 * FIXME: this should be documented, what is each thing.
 */
export const sentWaypointTelObjectSchema = new SimpleSchema({
  x: {
    type: Number,
    label: 'The latitude of the waypoint',
    min: -90,
    max: 90
  },
  y: {
    type: Number,
    label: 'The longitude of the waypoint',
    min: -180,
    max: 180
  },
  z: {
    type: Number,
    label: 'The altitude of the waypoint'
  },
  seq: {
    type: Number,
    label: 'The number of the waypoint in the list',
    min: 0
  },
  command: {
    type: Number,
    label: 'The type of the waypoint',
    allowedValues: [16, 21, 22, 206]
  },
  frame: {
    type: Number,
    label: 'The frame coordinate reference system'
  },
  param1: {
    type: Number,
    label: 'Parameter 1'
  },
  param2: {
    type: Number,
    label: 'Parameter 2'
  },
  param3: {
    type: Number,
    label: 'Parameter 3'
  },
  param4: {
    type: Number,
    label: 'Parameter 4'
  },
  featureNumber: {
    type: Number,
    label: 'The index this waypoint has in the FeatureCollection array',
    optional: true
  }
});

/**
 * Schema for the waypoint array that is read from to the RPA.
 * FIXME: this should be documented, what is each thing.
 */
const receivedWaypointTelObjectSchema = sentWaypointTelObjectSchema.pick(
  'x',
  'y',
  'z',
  'param1',
  'param2',
  'param3',
  'param4',
  'command',
  'seq'
);
receivedWaypointTelObjectSchema.extend({
  autocontinue: {
    type: Number,
    label: 'What is this?'
  },
  current: {
    type: Number,
    label: 'What is this?'
  },
  frame: {
    type: Number,
    label: 'The frame coordinate reference system'
  },
  mission_type: {
    type: Number,
    label: 'What is this?'
  },
  parameter: {
    type: String,
    label: 'What is this?'
  },
  target_component: {
    type: Number,
    label: 'What is this?'
  },
  target_system: {
    type: Number,
    label: 'What is this?'
  }
});

/**
 * Converts the waypoint array received from telemetry to GeoJSON Feature Collection.
 *
 * @param {Array} waypointArray - The waypoint list array from telemetry.
 * @returns {FeatureCollection<Point>|object} The waypoint list from the telemetry converted to
 * a GeoJSON object. Empty object if the array is empty. If any object in the array
 * does not have the correct format it's skipped.
 */
export const convertWaypointArrayToGeoJSON = waypointArray => {
  if (!isArray(waypointArray) || isEmpty(waypointArray)) {
    return {};
  }
  const newWayPointList = {
    type: 'FeatureCollection',
    features: []
  };
  for (let i = 1; i < waypointArray.length; i += 1) {
    try {
      receivedWaypointTelObjectSchema.validate(waypointArray[i]);
      const nextWPFeatureProps = {
        type: 5,
        autoPilotZ: waypointArray[i].z
      };
      const nextWPFeature = turf.point(
        [waypointArray[i].y, waypointArray[i].x],
        nextWPFeatureProps
      );
      if (newWayPointList.features.length > 0) {
        // break when landing point has been added to the resulting waypoint list
        if (newWayPointList.features[newWayPointList.features.length - 1].properties.type === 21) {
          break;
        }
      }
      // only update waypoint type if command is 16, 21 or 22
      if ([16, 21, 22, 206].indexOf(waypointArray[i].command) > -1) {
        switch (waypointArray[i].command) {
          case 21:
            nextWPFeature.properties.type = 2;
            break;
          case 22:
            nextWPFeature.properties.type = 1;
            break;
          case 16:
            nextWPFeature.properties.type = 5;
            break;
          case 206:
            // if command is 206 it should push any waypoint only change type of previous one inserted
            if (waypointArray[i].param1 === 0) {
              newWayPointList.features[newWayPointList.features.length - 1].properties.type = 4;
            } else {
              newWayPointList.features[newWayPointList.features.length - 1].properties.type = 3;
            }
            break;
          default:
            nextWPFeature.properties.type = 5;
            break;
        }
        // only push feature if (x,y,x) is different from (0,0,0)
        const waypointPosition = [waypointArray[i].x, waypointArray[i].y, waypointArray[i].z];
        if (!isEqual(waypointPosition, [0, 0, 0])) {
          newWayPointList.features.push(nextWPFeature);
        }
      } else {
        console.error(waypointArray[i]);
      }
    } catch (e) {
      console.error(e);
    }
  }
  return newWayPointList;
};

/**
 * Converts the waypoint FeatureCollection to an array of objects to send to
 * the RPA.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint FeatureCollection of Points.
 * @param {number} shootDistance - The shoot distance between photos in meters?
 * @param {string} waypointReferenceAltitude - The reference altitude for the waypoint height.
 * @param {number} takeOffSlope - The slope for takeoff for the RPA in %.
 * @returns {SentWaypoint[]} An array of objects representing the waypoints in a format that can
 * be read by the telemetry parser.
 */
export const convertWaypointFeatureCollection2TelemetryArray = (
  waypointList,
  shootDistance,
  waypointReferenceAltitude,
  takeOffSlope = 20
) => {
  const telemetryWaypointArray = [];
  const takeOffWaypoint = waypointList.features[0];
  const takeOffAltitude = getTakeOffElevation(waypointList);
  let frameForCoords = 0;

  // The magic waypoint
  const waypointHOME = {
    param1: 0,
    param2: 0,
    param3: 0,
    param4: 0,
    frame: 0, // Sea Level WGS84. Positive altitude over mean sea level (MSL)
    x: featurePointGetLatitude(takeOffWaypoint),
    y: featurePointGetLongitude(takeOffWaypoint),
    z: takeOffAltitude,
    seq: 0,
    command: 16
  };
  telemetryWaypointArray.push(waypointHOME);

  let seqNumber = 1;
  waypointList.features.forEach((waypoint, index) => {
    let waypointAltitude = getWaypointHeight(waypoint);
    if (isDTM(waypointReferenceAltitude)) {
      waypointAltitude += getWaypointGroundElevation(waypoint);
      // MAV_FRAME_GLOBAL
      frameForCoords = 0; // Sea Level WGS84. Positive altitude over mean sea level (MSL)
    } else if (isTakeOffGPS(waypointReferenceAltitude)) {
      // MAV_FRAME_GLOBAL_RELATIVE_ALT
      frameForCoords = 3; // Positive altitude with 0 being at the altitude of the home location
    } else {
      // MAV_FRAME_GLOBAL_RELATIVE_ALT
      frameForCoords = 3;
    }

    const newWPTelObject = {
      param1: 0,
      param2: 0,
      param3: 0,
      param4: 0,
      frame: frameForCoords,
      x: featurePointGetLatitude(waypoint),
      y: featurePointGetLongitude(waypoint),
      z: waypointAltitude,
      seq: seqNumber,
      command: 16,
      featureNumber: index
    };
    const newEventWPTelObject = {
      param1: 0,
      param2: 0,
      param3: 0,
      param4: 0,
      frame: frameForCoords,
      x: 0,
      y: 0,
      z: 0,
      seq: 0,
      command: ''
    };
    switch (waypoint.properties.type) {
      case 1:
        newWPTelObject.command = 22;
        newWPTelObject.param1 = takeOffSlope;
        break;
      case 2:
        newWPTelObject.command = 21;
        break;
      case 3:
        newWPTelObject.command = 16;
        newEventWPTelObject.command = 206;
        newEventWPTelObject.param1 = shootDistance;
        seqNumber += 1;
        newEventWPTelObject.seq = seqNumber;
        break;
      case 4:
        newWPTelObject.command = 16;
        newEventWPTelObject.command = 206;
        newEventWPTelObject.param1 = 0;
        seqNumber += 1;
        newEventWPTelObject.seq = seqNumber;
        break;
      case 5:
        newWPTelObject.command = 16;
        break;
      default:
        return;
    }
    telemetryWaypointArray.push(newWPTelObject);
    if ([3, 4].indexOf(waypoint.properties.type) > -1) {
      telemetryWaypointArray.push(newEventWPTelObject);
    }
    seqNumber += 1;
  });
  return telemetryWaypointArray;
};

/**
 * Takes in the waypoint list and return this waypoint list with the new height for the waypoint.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint List.
 * @param {number} waypointIndex - The index of the waypoint to change.
 * @param {number} newAbsAltitude - The new height for the waypoint in meters.
 * @returns {FeatureCollection<Point>} The new waypoint list with the new altitudes.
 */
export const changeWaypointAbsAltitude = (waypointList, waypointIndex, newAbsAltitude) => {
  const newWaypointList = cloneDeep(waypointList);
  newWaypointList.features[waypointIndex].properties.altAbsolute = newAbsAltitude;

  return newWaypointList;
};

/**
 * Takes in the waypoint list and returns a FeatureCollection representing the profile for that
 * waypoint list with a step defined in the parameters.
 * The loop to build the profile for each segment between waypoint has 2 exit conditions (AND):
 * - the distance to the next waypoint > step
 * - the last point in the segment is too close (closer than half the step) to the waypoint.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint List.
 * @param {number} step - The number of meters between each point in the path.
 * @returns {FeatureCollection<Point>} The profile built from that waypoint list.
 */
export const rpaPathProfile = (waypointList, step = 30) => {
  const rpaPathProfileFeatureCollection = {
    type: 'FeatureCollection',
    features: []
  };
  const profileArray = rpaPathProfileFeatureCollection.features;
  let totalDistanceTraveled = 0;
  const waypointArray = waypointList.features;
  try {
    for (let i = 0; i < waypointArray.length; i += 1) {
      if (profileArray.length > 0) {
        const distanceFromLastPoint = getDistanceToPoint(
          profileArray[profileArray.length - 1],
          waypointArray[i]
        );
        totalDistanceTraveled += distanceFromLastPoint;
      }
      const waypointProps = {
        distanceTraveled: totalDistanceTraveled,
        waypointNumber: i
      };
      const waypointFeature = turf.point(turf.getCoord(waypointArray[i]), waypointProps);
      profileArray.push(waypointFeature);
      if (i + 1 === waypointArray.length) {
        break;
      }
      const bearingToNextWp = getBearingToPoint(waypointArray[i], waypointArray[i + 1]);
      let distanceToNextWP = getDistanceToPoint(waypointArray[i], waypointArray[i + 1]);

      // this loop exectures while the distance to the next waypoint is greater than the step
      // the second condition tests if the last point in the segment is too close to the waypoint
      // if it's too closer than half the step then it goes into the next segment
      while (distanceToNextWP > step && Math.abs(distanceToNextWP - step) > step / 2) {
        const lastPointInProfile = profileArray[profileArray.length - 1];
        const nextPointInProfile = getDestinationPoint(lastPointInProfile, bearingToNextWp, step);
        distanceToNextWP = getDistanceToPoint(nextPointInProfile, waypointArray[i + 1]);
        totalDistanceTraveled += step;
        nextPointInProfile.properties.distanceTraveled = totalDistanceTraveled;
        profileArray.push(nextPointInProfile);
      }
    }
    return rpaPathProfileFeatureCollection;
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Takes in the elevation profile and updates the waypoint list altitudes,
 * it also calculates the distance travelled for each waypoint.
 *
 * @param {FeatureCollection<Point>} waypointFeatureCollection - The waypoint list.
 * @param {FeatureCollection<Point>} elevationProfile - The profile for the waypoint list.
 * @param {boolean} isPreFlight - Flag that indicates if in preflight component to skip
 * updating altitudes for takeoff and landing points since they should have the GPS data.
 * @returns {FeatureCollection<Point>} The waypointList with the ground altitude
 * in the properties section of each waypoint.
 */
export const updateAltitudesInWaypointList = (
  waypointFeatureCollection,
  elevationProfile,
  isPreFlight
) => {
  const waypointListCopy = cloneDeep(waypointFeatureCollection);
  const elevationProfileArray = elevationProfile.features;
  elevationProfileArray.forEach(profilePoint => {
    const waypointIndex = get(profilePoint, 'properties.waypointNumber');
    if (!isNil(waypointIndex)) {
      const waypointGroundElevation = turf.getCoord(profilePoint)[2];
      const waypointProps = waypointListCopy.features[waypointIndex].properties;
      const isTakeOff = profilePoint.properties.waypointNumber === 0;
      const isLanding =
        profilePoint.properties.waypointNumber === waypointListCopy.features.length - 1;
      //  skip updating ground elevation of takeoff and landing points
      //  since they should have the GPS data
      if (!isPreFlight || (isPreFlight && !(isTakeOff || isLanding))) {
        waypointProps.altGround = waypointGroundElevation;
      }
    }
  });
  return waypointListCopy;
};

/**
 * Takes in the elevation profile and updates the waypoint distances.
 *
 * @param {FeatureCollection<Point>} waypointFeatureCollection - The waypoint list.
 * @param {FeatureCollection<Point>} elevationProfile - The profile for the waypoint list.
 * @returns {FeatureCollection<Point>} The waypointList with the distance travelled in the
 * properties section of each waypoint.
 */
export const updateDistanceTravelledInWaypointList = (
  waypointFeatureCollection,
  elevationProfile
) => {
  const waypointListCopy = cloneDeep(waypointFeatureCollection);
  const elevationProfileArray = elevationProfile.features;
  elevationProfileArray.forEach(profilePoint => {
    const waypointIndex = get(profilePoint, 'properties.waypointNumber');
    if (!isNil(waypointIndex)) {
      const waypointDistanceTraveled = get(profilePoint, 'properties.distanceTraveled');
      const waypointProps = waypointListCopy.features[waypointIndex].properties;
      waypointProps.distanceTraveled = waypointDistanceTraveled;
    }
  });
  return waypointListCopy;
};

/**
 * Takes in the waypoint list and returns an array.
 * Each item in this array represents a segment in the flight profile.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @returns {Array.<[Feature<Point>, Feature<Point>]>} An array that contains the segments on the flight profile.
 */
export const splitFlightProfile = waypointList => {
  turf.collectionOf(waypointList, 'Point', 'splitFlightProfile');
  const flightProfileSplit = [];
  turf.featureEach(waypointList, (currentFeature, featureIndex) => {
    if (featureIndex < waypointList.features.length - 1) {
      flightProfileSplit.push([currentFeature, waypointList.features[featureIndex + 1]]);
    }
  });
  return flightProfileSplit;
};

/**
 * Takes in the waypoint list and returns an array with the slopes for each segment.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @param {string} waypointReferenceAltitude - The reference altitude for the waypoints.
 * @returns {number[]} An array that contains the slopes on the
 * flight profile or an emtpy array if something goes wrong.
 */
export const getWaypointListSlopes = (waypointList, waypointReferenceAltitude) => {
  try {
    turf.collectionOf(waypointList, 'Point', 'getWaypointListSlopes');
    const takeOffElevation = getTakeOffElevation(waypointList);
    const slopesArray = [];
    turf.featureEach(waypointList, (currentFeature, featureIndex) => {
      if (featureIndex < waypointList.features.length - 1) {
        let waypointAlt = getWaypointHeight(currentFeature);
        const waypointDist = getWaypointDistanceFromTakeOff(currentFeature);
        const nextWaypoint = waypointList.features[featureIndex + 1];
        let nextWaypointAlt = getWaypointHeight(nextWaypoint);
        const nextWaypointDist = getWaypointDistanceFromTakeOff(nextWaypoint);
        if (isTakeOffGPS(waypointReferenceAltitude) && takeOffElevation !== -999) {
          waypointAlt += takeOffElevation;
          nextWaypointAlt += takeOffElevation;
        } else if (isDTM(waypointReferenceAltitude)) {
          waypointAlt += getWaypointGroundElevation(currentFeature);
          nextWaypointAlt += getWaypointGroundElevation(nextWaypoint);
        }
        const segmentSlope =
          ((nextWaypointAlt - waypointAlt) / (nextWaypointDist - waypointDist)) * 100;
        slopesArray.push(round(segmentSlope));
      }
    });
    return slopesArray;
  } catch (error) {
    return [];
  }
};

/**
 * Takes in the waypoint list and returns an array with the slopes for each segment.
 * Only updates the slopes on the previous and next segments of the waypoint given.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @param {number[]} slopeArray - The old slope array.
 * @param {string} waypointReferenceAltitude - The reference altitude for the waypoints.
 * @param {number} wayPointIndex - The index of the waypoint changed.
 * @param {number} newWayPointAbsAltitude - The new height for the waypoint.
 * @returns {number[]} An array that contains the segments on the flight profile.
 */
export const updateWaypointListSlopes = (
  waypointList,
  slopeArray,
  waypointReferenceAltitude,
  wayPointIndex,
  newWayPointAbsAltitude
) => {
  const newSlopeArray = cloneDeep(slopeArray);
  const takeOffElevation = getTakeOffElevation(waypointList);
  if (wayPointIndex < waypointList.features.length - 1) {
    const waypoint = waypointList.features[wayPointIndex];
    let waypointAlt = newWayPointAbsAltitude;
    const waypointDist = getWaypointDistanceFromTakeOff(waypoint);
    const nextWaypoint = waypointList.features[wayPointIndex + 1];
    let nextWaypointAlt = getWaypointHeight(nextWaypoint);
    const nextWaypointDist = getWaypointDistanceFromTakeOff(nextWaypoint);
    if (isTakeOffGPS(waypointReferenceAltitude)) {
      waypointAlt += takeOffElevation;
      if (nextWaypoint.properties.type === 2 && getWaypointGroundElevation(nextWaypoint) !== -999) {
        nextWaypointAlt += getWaypointGroundElevation(nextWaypoint);
      } else {
        nextWaypointAlt += takeOffElevation;
      }
    } else if (isDTM(waypointReferenceAltitude)) {
      waypointAlt += getWaypointGroundElevation(waypoint);
      nextWaypointAlt += getWaypointGroundElevation(nextWaypoint);
    }
    const nextSlope = ((nextWaypointAlt - waypointAlt) / (nextWaypointDist - waypointDist)) * 100;
    newSlopeArray[wayPointIndex] = round(nextSlope);
    if (wayPointIndex !== 0) {
      const prevWaypoint = waypointList.features[wayPointIndex - 1];
      let prevWaypointAlt = getWaypointHeight(prevWaypoint);
      const prevWaypointDist = getWaypointDistanceFromTakeOff(prevWaypoint);
      if (isTakeOffGPS(waypointReferenceAltitude)) {
        prevWaypointAlt += takeOffElevation;
      } else if (isDTM(waypointReferenceAltitude)) {
        prevWaypointAlt += getWaypointGroundElevation(waypoint);
      }
      const prevSlope = ((waypointAlt - prevWaypointAlt) / (waypointDist - prevWaypointDist)) * 100;
      newSlopeArray[wayPointIndex - 1] = round(prevSlope);
    }
  }
  return newSlopeArray;
};

/**
 * Takes in the waypoint list updates the 3rd coordinate for each waypoint so that it reflect the
 * correct altitude depending on the waypointReferenceAltitude.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @param {string} waypointReferenceAltitude - The reference altitude for the waypoints.
 * @returns {FeatureCollection<Point>} The waypoint list with the 3rd coordinate updated to reflect
 * the correct altitude.
 */
export const updateWaypointCoordsForExport = (waypointList, waypointReferenceAltitude) => {
  const takeOffAltitude = getTakeOffElevation(waypointList);
  const waypointListUpdated = cloneDeep(waypointList);
  turf.featureEach(waypointListUpdated, currentFeature => {
    featurePointSetAltitude(
      currentFeature,
      getWaypointAltitude(currentFeature, waypointReferenceAltitude, takeOffAltitude)
    );
  });
  return waypointListUpdated;
};

/**
 * Takes in the mission and returns a kml formatted string.
 *
 * @param {object} missionToExport - The mission object to export.
 * @param {FeatureCollection<Point>} missionToExport.waypointList - The waypoint list.
 * @param {Feature<LineString>} missionToExport.rpaPath - The rpa path.
 * @param {Feature<LineString>|Feature<Polygon>} missionToExport.geometry - The mission geometry.
 * @returns {string} A kml formatted string with the mission information.
 * @throws Asserts that waypointList, rpaPath, geometry are correct geojson entities if
 * they are defined and not empty.
 */
export const missionToKML = missionToExport => {
  let kmlMission = '';
  if (!isNil(missionToExport.waypointList) && !isEmpty(missionToExport.waypointList)) {
    turf.collectionOf(missionToExport.waypointList, 'Point', 'missionToKML');
    const kmlwaypointlist = featureCollectionPoints2KML(missionToExport.waypointList, 'absolute');
    kmlMission += kmlwaypointlist;
  }
  if (!isNil(missionToExport.rpaPath) && !isEmpty(missionToExport.rpaPath)) {
    turf.featureOf(missionToExport.rpaPath, 'LineString', 'missionToKML');
    const kmlPath = featureLinestring2KML(missionToExport.rpaPath, 'absolute');
    kmlMission += kmlPath;
  }
  if (!isNil(missionToExport.geometry) && !isEmpty(missionToExport.geometry)) {
    let kmlGeometry = '';
    switch (turf.getType(missionToExport.geometry)) {
      case 'LineString':
        turf.featureOf(missionToExport.geometry, 'LineString', 'missionToKML');
        kmlGeometry = featureLinestring2KML(missionToExport.geometry);
        break;
      case 'Polygon':
        turf.featureOf(missionToExport.geometry, 'Polygon', 'missionToKML');
        kmlGeometry = featurePolygon2KML(missionToExport.geometry);
        break;
      default:
        console.error('wrong geometry');
        break;
    }
    kmlMission += kmlGeometry;
  }
  return `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>${kmlMission}</Document></kml>`;
};

/**
 * Takes in the elevationProfile and returns a kml formatted string.
 *
 * @param {FeatureCollection<Point>} elevationProfile - The elevation profile.
 * @param {string} [altitudeMode='clampToGround'] - The reference altitude for the waypoints.
 * @returns {string} A kml formatted string with the elevation profile.
 */
export const elevationProfileToKML = (elevationProfile, altitudeMode = 'clampToGround') => {
  turf.collectionOf(elevationProfile, 'Point', 'elevationProfileToKML');
  const kmlProfile = featureCollectionPoints2KML(elevationProfile, altitudeMode);
  return `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>${kmlProfile}</Document></kml>`;
};

/**
 * Takes in the waypoint list and the waypoint reference altitude and returns the highest
 * elevation for that waypoint list.
 *
 * @param {FeatureCollection<Point>} waypointList - The waypoint list.
 * @param {string} waypointReferenceAltitude - The reference altitude for the waypoints.
 * @returns {number} The hightest altitude in the waypoint list.
 * @throws Error is waypointList is not a feature collection of points.
 */
export const getWaypointListHighestAltitude = (waypointList, waypointReferenceAltitude) => {
  turf.collectionOf(waypointList, 'Point', 'getWaypointListHighestAltitude');
  const takeOffElevation = getTakeOffElevation(waypointList);
  let highestAltitude = 0;
  turf.featureEach(waypointList, currentFeature => {
    const waypointAltitude = getWaypointAltitude(
      currentFeature,
      waypointReferenceAltitude,
      takeOffElevation
    );
    if (highestAltitude < waypointAltitude) {
      highestAltitude = waypointAltitude;
    }
  });

  return round(highestAltitude, 2);
};

export default {
  setWaypointNumbers,
  getOperationType,
  editWayPointType,
  insertNewWaypointsAtIndex,
  removeWaypoint,
  moveWaypoint,
  getLandingBearing,
  getWaypointAltitude,
  getWaypointHeight,
  getWaypointNumber,
  getWaypointType,
  getWaypointGroundElevation,
  checkforLandingPath,
  getTakeOffElevation,
  getLastWaypointFeaturePoint,
  getLandingPoint,
  addLandingPath,
  deleteLandingPath,
  getDistanceTraveled,
  sentWaypointTelObjectSchema,
  convertWaypointArrayToGeoJSON,
  convertWaypointFeatureCollection2TelemetryArray,
  changeWaypointAbsAltitude,
  rpaPathProfile,
  updateAltitudesInWaypointList,
  updateDistanceTravelledInWaypointList,
  splitFlightProfile,
  getWaypointListSlopes,
  updateWaypointListSlopes,
  updateWaypointCoordsForExport,
  missionToKML,
  elevationProfileToKML,
  isDTM,
  isTakeOffGPS,
  getWaypointListHighestAltitude
};
